import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import api from '../adapters/api';
import { AuthContext } from '../context/AuthContext';
import { CartContext } from '../context/CartContext';

const escapeHtml = (s) =>
  String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const formatRemaining = (ms) => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const ss = String(total % 60).padStart(2, '0');
  return `${Math.floor(total / 60)}:${ss}`;
};

// รอจน frontend (CRA dev server) ตอบได้อีกครั้งหลัง deploy แล้วค่อยรีโหลดหน้า
const reloadWhenReady = async () => {
  for (;;) {
    try {
      const res = await fetch('/', { cache: 'no-store' });
      if (res.ok) {
        window.location.reload();
        return;
      }
    } catch { /* server ยังไม่ขึ้น */ }
    await new Promise(r => setTimeout(r, 3000));
  }
};

export const showDeployLog = async () => {
  try {
    const { data } = await api.get('/api/system/deploy-log');
    await Swal.fire({
      icon: 'info',
      title: data.file ? `Log: ${data.file}` : 'ยังไม่มี log การอัปเดต',
      width: 800,
      html: data.lines?.length
        ? `<pre style="text-align:left;max-height:400px;overflow:auto;font-size:12px;">${escapeHtml(data.lines.join('\n'))}</pre>`
        : '',
      confirmButtonColor: '#1976d2',
      confirmButtonText: 'ตกลง',
    });
  } catch (err) {
    console.error('deploy-log error:', err);
  }
};

// NavBar mount ใหม่ทุกครั้งที่เปลี่ยนหน้า — จำไว้ระดับ module ว่าเด้ง popup อัตโนมัติของรอบไหนไปแล้ว
let popupShownFor = null;
let countdownPopupOpen = false;

const RING_R = 54;
const RING_C = 2 * Math.PI * RING_R;
const WARN = '#ed6c02';
const DANGER = '#d32f2f';

const POPUP_CSS = `
  .rc-wrap { font-size: 15px; color: #37474f; }
  .rc-ring { position: relative; width: 140px; height: 140px; margin: 4px auto 10px; }
  .rc-ring svg { transform: rotate(-90deg); }
  .rc-ring .rc-track { stroke: #fbe9e7; }
  .rc-ring .rc-arc { transition: stroke-dashoffset 1s linear, stroke .3s; }
  .rc-time { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
  .rc-time b { font-size: 36px; line-height: 1; font-variant-numeric: tabular-nums; }
  .rc-time span { font-size: 13px; color: #78909c; margin-top: 4px; }
  .rc-at { font-size: 14px; color: #607d8b; margin-bottom: 14px; }
  .rc-note { text-align: left; background: #fff8e1; border: 1px solid #ffe0b2; border-radius: 10px; padding: 10px 12px; margin-bottom: 12px; }
  .rc-note-h { font-size: 12px; font-weight: 700; color: ${WARN}; margin-bottom: 4px; letter-spacing: .2px; }
  .rc-note-b { white-space: pre-wrap; word-break: break-word; color: #37474f; max-height: 40vh; overflow-y: auto; }
  .rc-tips { list-style: none; margin: 0; padding: 0; text-align: left; font-size: 14px; }
  .rc-tips li { display: flex; gap: 8px; align-items: flex-start; padding: 5px 0; }
  .rc-tips li + li { border-top: 1px dashed #eceff1; }
  .rc-dot { flex: none; width: 20px; height: 20px; border-radius: 50%; background: #e3f2fd; color: #1976d2; font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; }
`;

const formatClock = (ms) =>
  new Date(ms).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });

// force = เปิดเองจากการคลิกตัวนับบน navbar (ไม่สนว่าเคยเด้งรอบนี้แล้ว)
const openCountdownPopup = (info, clockOffset, { force = false, isDev = false, onCancelRestart } = {}) => {
  const { restartAt, scheduledAt, note } = info;
  if (!force && popupShownFor === restartAt) return;
  popupShownFor = restartAt;
  countdownPopupOpen = true;
  const total = Math.max(1, restartAt - (scheduledAt || restartAt - 5 * 60 * 1000));
  const remainingNow = () => restartAt - (Date.now() + clockOffset.current);
  // เวลาเริ่มปรับปรุงตามนาฬิกาเครื่องผู้ใช้ (ชดเชย offset กับ server แล้ว)
  const localRestartAt = restartAt - clockOffset.current;
  let intervalId;

  Swal.fire({
    title: 'แจ้งปิดปรับปรุงระบบ',
    width: 420,
    html: `
      <style>${POPUP_CSS}</style>
      <div class="rc-wrap">
        <div class="rc-ring">
          <svg width="140" height="140" viewBox="0 0 140 140">
            <circle class="rc-track" cx="70" cy="70" r="${RING_R}" fill="none" stroke-width="10" />
            <circle class="rc-arc" cx="70" cy="70" r="${RING_R}" fill="none" stroke-width="10"
              stroke-linecap="round" stroke="${WARN}" stroke-dasharray="${RING_C}" stroke-dashoffset="0" />
          </svg>
          <div class="rc-time"><b>${formatRemaining(remainingNow())}</b><span>นาที</span></div>
        </div>
        <div class="rc-at">ระบบจะเริ่มปรับปรุงเวลา <b>${formatClock(localRestartAt)} น.</b></div>
        ${note ? `
        <div class="rc-note">
          <div class="rc-note-h">📢 ประกาศจากผู้ดูแลระบบ</div>
          <div class="rc-note-b">${escapeHtml(note)}</div>
        </div>` : ''}
        <ul class="rc-tips">
          <li><span class="rc-dot">1</span><span>บันทึกงานหรือเอกสารที่ทำค้างไว้ก่อนหมดเวลา</span></li>
          <li><span class="rc-dot">2</span><span>ระหว่างอัปเดตจะใช้งานระบบไม่ได้ชั่วคราว หน้าเว็บจะรีโหลดเองเมื่อพร้อม</span></li>
          <li><span class="rc-dot">3</span><span>หลังอัปเดตเสร็จต้องเข้าสู่ระบบใหม่</span></li>
        </ul>
      </div>`,
    confirmButtonColor: '#1976d2',
    confirmButtonText: 'รับทราบ',
    // dev ยกเลิกได้จาก popup นี้ (ปุ่ม deny จะถามยืนยันอีกชั้นใน onCancelRestart)
    showDenyButton: isDev,
    denyButtonText: 'ยกเลิกรีสตาร์ท',
    denyButtonColor: '#757575',
    didOpen: (popup) => {
      const timeEl = popup.querySelector('.rc-time b');
      const arcEl = popup.querySelector('.rc-arc');
      const update = () => {
        const left = remainingNow();
        const color = left <= 60 * 1000 ? DANGER : WARN;
        if (timeEl) {
          timeEl.textContent = formatRemaining(left);
          timeEl.style.color = color;
        }
        if (arcEl) {
          const frac = Math.min(1, Math.max(0, left / total));
          arcEl.setAttribute('stroke-dashoffset', String(RING_C * (1 - frac)));
          arcEl.setAttribute('stroke', color);
        }
      };
      update();
      intervalId = setInterval(update, 1000);
    },
    didDestroy: () => {
      clearInterval(intervalId);
      countdownPopupOpen = false;
    },
  }).then(result => {
    if (result.isDenied && onCancelRestart) onCancelRestart();
  });
};

const closeCountdownPopup = () => {
  if (countdownPopupOpen) Swal.close();
};

// ตัวนับ "restart m:ss" บน NavBar + popup แจ้งเตือน + หน้าจอรอระหว่าง deploy
export default function RestartCountdown() {
  const { currentUser } = useContext(AuthContext);
  const { addSystemListener } = useContext(CartContext);
  const [phase, setPhase] = useState('idle');
  const [restartAt, setRestartAt] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const clockOffset = useRef(0); // serverNow - Date.now() กันนาฬิกาเครื่องผู้ใช้เพี้ยน
  const phaseRef = useRef('idle');
  const reloading = useRef(false);
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;
  const confirmCancelRef = useRef(null);
  const infoRef = useRef({}); // { restartAt, scheduledAt, note } ของรอบปัจจุบัน ใช้เปิด popup ซ้ำตอนคลิกตัวนับ

  const applyStatus = useCallback((s) => {
    if (typeof s.serverNow === 'number') clockOffset.current = s.serverNow - Date.now();
    phaseRef.current = s.phase;
    setPhase(s.phase);
    setRestartAt(s.restartAt ?? null);
    if (s.phase === 'scheduled' && s.restartAt) {
      infoRef.current = { restartAt: s.restartAt, scheduledAt: s.scheduledAt, note: s.note };
      // ผู้ใช้ที่เพิ่งเปิดหน้า/login ระหว่างนับถอยหลังก็ได้เห็น popup ด้วย (ครั้งเดียวต่อรอบ)
      openCountdownPopup(infoRef.current, clockOffset, {
        isDev: currentUserRef.current?.is_dev,
        onCancelRestart: () => confirmCancelRef.current?.(),
      });
    } else {
      closeCountdownPopup();
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/api/system/restart-status');
      return data;
    } catch (err) {
      console.error('restart-status error:', err);
      return null;
    }
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    fetchStatus().then(s => s && applyStatus(s));

    return addSystemListener(async (message) => {
      switch (message.type) {
        case 'RESTART_SCHEDULED':
          applyStatus(message);
          break;
        case 'RESTART_CANCELLED':
          applyStatus({ phase: 'idle', restartAt: null, note: null });
          Swal.fire({ icon: 'info', title: 'ยกเลิกการปิดปรับปรุงระบบแล้ว', timer: 1500, showConfirmButton: false });
          break;
        case 'RESTART_STARTED':
          applyStatus({ phase: 'deploying', restartAt: null });
          break;
        case 'RESTART_FAILED':
          applyStatus({ phase: 'idle', restartAt: null });
          if (currentUser.is_dev) {
            const result = await Swal.fire({
              icon: 'error',
              title: 'อัปเดตระบบไม่สำเร็จ',
              text: `${message.reason ? `${message.reason} — ` : ''}ระบบยังทำงานด้วยโค้ดเดิม`,
              showCancelButton: true,
              confirmButtonColor: '#1976d2',
              cancelButtonColor: '#757575',
              confirmButtonText: 'ดู log',
              cancelButtonText: 'ยกเลิก',
            });
            if (result.isConfirmed) showDeployLog();
          }
          break;
        case 'WS_RECONNECTED': {
          // ระหว่าง deploy ปล่อยให้ effect ด้านล่าง poll แทน
          if (phaseRef.current === 'deploying') break;
          const s = await fetchStatus();
          if (s) applyStatus(s);
          break;
        }
        default:
          break;
      }
    });
  }, [currentUser, addSystemListener, applyStatus, fetchStatus]);

  // session เก็บใน memory ของ backend — พอ restart แล้ว WebSocket ต่อกลับไม่ได้ (ต้องมี session)
  // จึง poll ผ่าน HTTP: ได้ 401 หรือ phase ไม่ใช่ deploying แปลว่าเป็น backend ตัวใหม่แล้ว → รีโหลดหน้า
  useEffect(() => {
    if (phase !== 'deploying') return;
    const id = setInterval(async () => {
      if (reloading.current) return;
      let isNewServer = false;
      try {
        const { data } = await api.get('/api/system/restart-status');
        isNewServer = data.phase !== 'deploying';
      } catch (err) {
        isNewServer = err.response?.status === 401;
      }
      if (isNewServer) {
        reloading.current = true;
        reloadWhenReady();
      }
    }, 5000);
    return () => clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'scheduled' || !restartAt) return;
    const tick = () => setRemaining(restartAt - (Date.now() + clockOffset.current));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [phase, restartAt]);

  const confirmCancel = async () => {
    const confirm = await Swal.fire({
      icon: 'question',
      title: 'ยกเลิกการรีสตาร์ทระบบ?',
      showCancelButton: true,
      confirmButtonColor: '#1976d2',
      cancelButtonColor: '#757575',
      confirmButtonText: 'ยกเลิกรีสตาร์ท',
      cancelButtonText: 'ยกเลิก',
    });
    if (!confirm.isConfirmed) return;
    try {
      // แจ้งผลผ่าน broadcast RESTART_CANCELLED เหมือนผู้ใช้คนอื่น
      await api.post('/api/system/restart/cancel');
    } catch (err) {
      console.error('cancel restart error:', err);
      Swal.fire({
        icon: 'error',
        title: 'ยกเลิกไม่สำเร็จ',
        text: err.response?.data?.error || 'เกิดข้อผิดพลาด',
        confirmButtonColor: '#1976d2',
        confirmButtonText: 'ตกลง',
      });
    }
  };

  confirmCancelRef.current = confirmCancel;

  if (!currentUser) return null;

  const openDetails = () =>
    openCountdownPopup(infoRef.current, clockOffset, {
      force: true,
      isDev: currentUser.is_dev,
      onCancelRestart: confirmCancel,
    });

  return (
    <>
      {phase === 'scheduled' && (
        <Tooltip title="ดูรายละเอียดการปิดปรับปรุงระบบ">
          <Box
            component="button"
            type="button"
            onClick={openDetails}
            aria-label={`ระบบจะรีสตาร์ทในอีก ${formatRemaining(remaining)} นาที`}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              height: 36,
              px: { xs: 1, sm: 1.25 },
              border: 0,
              borderRadius: 18,
              bgcolor: remaining <= 60 * 1000 ? 'error.main' : 'warning.main',
              color: '#fff',
              font: 'inherit',
              fontSize: '0.85rem',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              '&:hover': { filter: 'brightness(0.92)' },
            }}
          >
            <RestartAltIcon sx={{ fontSize: 18 }} />
            {/* มือถือพื้นที่ navbar น้อย — แสดงแค่ไอคอน + เวลา */}
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>restart</Box>
            {formatRemaining(remaining)}
          </Box>
        </Tooltip>
      )}
      <Backdrop open={phase === 'deploying'} sx={{ color: '#fff', zIndex: (t) => t.zIndex.modal + 10, flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" />
        <Typography variant="h6">ระบบกำลังอัปเดต กรุณารอสักครู่...</Typography>
        <Typography variant="body2">หน้าเว็บจะรีโหลดเองเมื่อระบบพร้อมใช้งาน</Typography>
      </Backdrop>
    </>
  );
}
